import { Injectable } from '@angular/core';
import { Observable, Subject, tap } from 'rxjs';
import { ReportSettings } from 'src/app/core/models/report-settings.interface';
import { UserService } from '../open-api/user.service';
import { CurrentUser } from '../../models/user';
import { distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { CloneUtils } from '../../utils/clone-utils';
import { DataUtils } from 'src/app/core/utils/data-utils';
import { ReportSettingDefaults } from '@/core/services/report-settings/report-setting-defaults';
import { isNullOrUndefined } from '@/core/utils/generic-utils';
import { UserSettings } from '@/core/models/user-settings';

@Injectable({
    providedIn: 'root',
})
export class ReportSettingsService {
    private readonly defaultSettings: ReportSettings;
    private readonly reportSettingsChanged = new Subject<ReportSettings>();
    /**
     * The report settings of the current user.
     * Use this observable for reading the user's settings.
     * For non-pro-users, it contains the default settings (or adjusted settings).
     * For pro-users, it contains the real user's settings as stored in the user's settings,
     * but with default values initialized when nothing is stored / customized yet.
     * Attention: This settings object is not safe to be read and stored for non-pro-users.
     */
    public readonly currentReportSettings$: Observable<ReportSettings>;
    /**
     * An observable that emits when the report settings of the current user change (after a successful API update call)
     */
    public readonly reportSettingsChanged$: Observable<ReportSettings> = this.reportSettingsChanged.asObservable();
    // a copy of the settings object for change detection and to retrieve an unmodified copy of the current settings
    private settingsSnapshot: ReportSettings | undefined = undefined;

    constructor(private readonly userService: UserService) {
        this.defaultSettings = CloneUtils.cloneData(ReportSettingDefaults.getDefaultSettings());

        this.currentReportSettings$ = this.userService.currentUser$.pipe(
            // filter((user) => this.haveSettingsChanged(user)),
            distinctUntilChanged((prev, curr) =>
                DataUtils.isEqual(prev.settings.reportSettings, curr.settings.reportSettings),
            ),
            tap((user) => this.updateSettingsSnapshot(user)),
            map((user) => this.processCurrentUserChange(user)),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
    }

    private updateSettingsSnapshot(user: CurrentUser): void {
        const curSettings = user.settings.reportSettings;
        this.settingsSnapshot = curSettings === undefined ? undefined : CloneUtils.cloneData(curSettings);
    }

    private processCurrentUserChange(user: CurrentUser): ReportSettings {
        const finalSettings = CloneUtils.cloneData(this.defaultSettings);
        if (!user.isPro()) {
            // Not-pro users have default settings, only pro-accounts may modify their report settings.
            // When a pro-user falls back to the free tier, the default settings will be used again.
            return finalSettings;
        }
        if (user && user.settings && user.settings.reportSettings) {
            // special initialization with default values
            const settingsCopy = CloneUtils.cloneData(user.settings.reportSettings);
            const cleanSettings = DataUtils.removeNullFields(settingsCopy);
            if (!cleanSettings.tableColumns || cleanSettings.tableColumns.length === 0) {
                cleanSettings.tableColumns = finalSettings.tableColumns;
            }
            DataUtils.patchObject(finalSettings, cleanSettings);
        }
        return finalSettings;
    }

    /**
     * Updates the current users report settings in a patch way. It accepts Partial<ReportSettings> and only updates
     * those that are present in the object.
     * @param reportSettings The partial report settings to update
     * @returns An observable that emits the updated user when the API update is successful
     */
    public updateReportSettings(reportSettings: Partial<ReportSettings>): Observable<CurrentUser> {
        const copy = CloneUtils.cloneData(reportSettings);
        this.handleRoundingEnabled(copy);

        return this.userService.loadCurrentUser(true).pipe(
            switchMap((user) => {
                // The backend replaces reportSettings. Preserve the other options and user settings,
                // but omit unrelated user fields so legacy names cannot block this operation (#747).
                return this.userService
                    .patchCurrentUser({
                        settings: UserSettings.toJson(
                            UserSettings.mergeUpdate(user.settings, { reportSettings: copy }),
                        ),
                    })
                    .pipe(
                        tap((updatedUser) => {
                            this.reportSettingsChanged.next(updatedUser.settings.reportSettings);
                        }),
                    );
            }),
        );
    }

    private handleRoundingEnabled(update: Partial<ReportSettings>): void {
        if (this.settingsSnapshot === undefined) {
            console.error('ReportSettingsService.handleRoundingEnabled() - settingsSnapshot is undefined!');
            return;
        }
        const current = this.settingsSnapshot;
        const roundingEnabledChanged =
            update.roundingEnabled !== undefined && update.roundingEnabled !== current.roundingEnabled;
        if (roundingEnabledChanged) {
            const roundingUnitNotDefined = isNullOrUndefined(update.durationRoundingUnit);
            const roundingTypeNotDefined = isNullOrUndefined(update.durationRoundingType);
            if (update.roundingEnabled) {
                if (roundingUnitNotDefined && current.durationRoundingUnit === null) {
                    update.durationRoundingUnit = ReportSettingDefaults.getDefaultRoundingUnitNumber();
                }
                if (roundingTypeNotDefined && current.durationRoundingType === null) {
                    update.durationRoundingType = ReportSettingDefaults.getDefaultRoundingType();
                }
            } else {
                // TODO - clarify if we want to reset or keep the settings
                if (roundingUnitNotDefined && current.durationRoundingUnit !== null) {
                    update.durationRoundingUnit = null;
                }
                if (roundingTypeNotDefined && current.durationRoundingType !== null) {
                    update.durationRoundingType = null;
                }
            }
        }
    }

    public getNotModified(): ReportSettings | undefined {
        if (this.settingsSnapshot === undefined) {
            return undefined;
        }
        return CloneUtils.cloneData(this.settingsSnapshot);
    }
}
